import React, { useEffect, useState } from "react";
import { Bell, BellOff, AlertCircle, CheckCircle2, Loader2, Send } from "lucide-react";
import {
  pushSupported,
  getPushStatus,
  subscribeToPush,
  unsubscribeFromPush,
  sendTestPush,
} from "@/lib/push";

/**
 * Self-contained card that shows current push status and exposes Enable / Disable /
 * Send test buttons. Usable from both provider Profile and patient Mobile Settings.
 *
 * The card hides itself when the browser does not support Push at all (e.g. older
 * Safari on iOS < 16.4) so we never show a dead toggle.
 */
export default function NotificationsPanel({ compact = false }) {
  const [status, setStatus] = useState({ supported: false, permission: "default", subscribed: false });
  const [busy, setBusy]     = useState(false);
  const [msg, setMsg]       = useState({ kind: "", text: "" });

  const refresh = async () => {
    const s = await getPushStatus();
    setStatus(s);
  };
  useEffect(() => { refresh(); }, []);

  const flash = (kind, text) => { setMsg({ kind, text }); setTimeout(() => setMsg({ kind: "", text: "" }), 4500); };

  const onEnable = async () => {
    setBusy(true);
    try {
      const r = await subscribeToPush();
      if (r.ok) flash("success", "Notifications enabled on this device.");
      else      flash("error",   r.reason || "Could not enable notifications.");
      await refresh();
    } catch (e) {
      flash("error", e?.message || "Could not enable notifications.");
    } finally {
      setBusy(false);
    }
  };

  const onDisable = async () => {
    setBusy(true);
    try {
      await unsubscribeFromPush();
      flash("success", "Notifications disabled on this device.");
      await refresh();
    } catch (e) {
      flash("error", e?.message || "Could not disable notifications.");
    } finally {
      setBusy(false);
    }
  };

  const onTest = async () => {
    setBusy(true);
    try {
      const r = await sendTestPush();
      const total = (r?.sent_web || 0) + (r?.sent_fcm || 0) + (r?.sent_apns || 0);
      if (total > 0) flash("success", `Test sent to ${total} device(s). Check your notification tray.`);
      else if (r?.no_devices) flash("error", "No registered devices — enable notifications first.");
      else if (r?.failed) flash("error", "Send failed. Subscription may have expired — try toggling off and back on.");
      else flash("error", "Test could not be sent.");
    } catch (e) {
      flash("error", e?.message || "Test failed.");
    } finally {
      setBusy(false);
    }
  };

  if (!status.supported) {
    return (
      <div className={compact ? "rounded-2xl border border-stone-200 bg-white p-5" : "bg-white border border-stone-200 rounded-xl shadow-soft p-6 max-w-xl"} data-testid="notifications-unsupported">
        <div className="flex items-start gap-3">
          <BellOff className="w-5 h-5 text-stone-400 mt-0.5" />
          <div>
            <div className="font-semibold text-stone-900">Browser notifications</div>
            <p className="text-sm text-stone-500 mt-1">
              This browser doesn&apos;t support web push. Try Chrome, Edge, Firefox, or Safari 16.4+ on iOS (add to Home Screen first).
            </p>
          </div>
        </div>
      </div>
    );
  }

  const permissionLabel = {
    default: "Not requested yet",
    granted: "Allowed",
    denied:  "Blocked in browser",
  }[status.permission] || status.permission;

  return (
    <div className={compact ? "rounded-2xl border border-stone-200 bg-white p-5" : "bg-white border border-stone-200 rounded-xl shadow-soft p-6 max-w-xl"} data-testid="notifications-panel">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-brand-100 text-brand-700 flex items-center justify-center shrink-0">
          <Bell className="w-5 h-5" />
        </div>
        <div>
          <div className="font-semibold text-stone-900">Browser notifications</div>
          <p className="text-sm text-stone-500 mt-0.5">
            Get a notification 24 hours before your appointment, plus reschedule decisions in real-time.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs mb-4">
        <div className="rounded-lg bg-stone-50 border border-stone-200 px-3 py-2">
          <div className="text-stone-500">Permission</div>
          <div className="font-semibold text-stone-900 mt-0.5" data-testid="push-permission">{permissionLabel}</div>
        </div>
        <div className="rounded-lg bg-stone-50 border border-stone-200 px-3 py-2">
          <div className="text-stone-500">This device</div>
          <div className="font-semibold text-stone-900 mt-0.5" data-testid="push-subscribed">
            {status.subscribed ? "Subscribed" : "Not subscribed"}
          </div>
        </div>
      </div>

      {status.permission === "denied" && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 mb-3 flex items-start gap-2" data-testid="push-blocked-note">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>Notifications are blocked at the browser level. Click the lock icon in the address bar → Site settings → Notifications → Allow, then refresh.</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {!status.subscribed ? (
          <button
            onClick={onEnable}
            disabled={busy || status.permission === "denied"}
            data-testid="push-enable"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-brand-500 hover:bg-brand-700 text-white font-semibold text-sm disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />} Enable notifications
          </button>
        ) : (
          <>
            <button
              onClick={onTest}
              disabled={busy}
              data-testid="push-test"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-brand-500 hover:bg-brand-700 text-white font-semibold text-sm disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send test
            </button>
            <button
              onClick={onDisable}
              disabled={busy}
              data-testid="push-disable"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-white border border-stone-300 text-stone-700 hover:bg-stone-50 font-semibold text-sm disabled:opacity-50"
            >
              <BellOff className="w-4 h-4" /> Turn off
            </button>
          </>
        )}
      </div>

      {msg.text && (
        <div
          data-testid={`push-msg-${msg.kind}`}
          className={`mt-3 flex items-center gap-2 px-3 py-2 rounded-lg text-sm border ${
            msg.kind === "success"
              ? "bg-emerald-50 border-emerald-200 text-emerald-700"
              : "bg-red-50 border-red-200 text-red-700"
          }`}
        >
          {msg.kind === "success" ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
          <span>{msg.text}</span>
        </div>
      )}
    </div>
  );
}
